import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { safeFormatDate, formatDateTimeBerlin } from '../lib/dateTime';
import { getErrorMessage } from '../lib/errorMessage';
import {
  UserCheck,
  UserX,
  Users,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  Gavel,
  MessageSquare,
  Edit3,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// ============================================================
// VERTRETUNG KOMPONENTEN
// ============================================================

/**
 * DeputyActivationPanel - Vorgesetzter aktiviert/deaktiviert seine Vertretung
 */
export const DeputyActivationPanel = () => {
  const { api, user } = useAuth();
  const [supervisors, setSupervisors] = useState([]);
  const [myDeputies, setMyDeputies] = useState([]);
  const [actingFor, setActingFor] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    deputy_id: '',
    valid_from: '',
    valid_to: '',
    note: '',
  });

  const canManageDeputy = user?.role === 'supervisor' || user?.role === 'admin';

  useEffect(() => {
    if (canManageDeputy) {
      fetchData();
    }
  }, [canManageDeputy]);

  const fetchData = async () => {
    try {
      const [deputyRes, actingForRes, usersRes] = await Promise.all([
        api.get('/approvals/deputy/mine'),
        api.get('/approvals/deputy/acting-for'),
        api.get('/users'),
      ]);
      
      setMyDeputies(deputyRes.data);
      setActingFor(actingForRes.data.acting_for || []);
      
      // Nur Supervisors und Admins als mögliche Vertreter
      const potentialDeputies = usersRes.data.filter(
        u => (u.role === 'supervisor' || u.role === 'admin') && u.id !== user?.id
      );
      setSupervisors(potentialDeputies);
    } catch (error) {
      console.error('Failed to fetch deputy data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!formData.deputy_id) {
      toast.error('Bitte wählen Sie einen Vertreter aus');
      return;
    }

    try {
      await api.post('/approvals/deputy/activate', formData);
      toast.success('Vertretung aktiviert');
      setDialogOpen(false);
      setFormData({ deputy_id: '', valid_from: '', valid_to: '', note: '' });
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Aktivieren';
      toast.error(message);
    }
  };

  const handleDeactivate = async (assignmentId) => {
    try {
      await api.post(`/approvals/deputy/deactivate/${assignmentId}`);
      toast.success('Vertretung deaktiviert');
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Deaktivieren');
    }
  };

  if (!canManageDeputy) return null;
  if (isLoading) return <div className="text-muted-foreground text-sm">Lade Vertretungsdaten...</div>;

  const activeDeputies = myDeputies.filter(d => d.is_currently_active);

  return (
    <div className="space-y-4">
      {/* Hinweis: Vertritt aktuell jemanden */}
      {actingFor.length > 0 && (
        <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/30">
          <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 mb-2">
            <UserCheck className="w-5 h-5" />
            <span className="font-medium">Sie vertreten aktuell:</span>
          </div>
          <div className="space-y-1">
            {actingFor.map(af => (
              <div key={af.assignment_id} className="text-sm text-muted-foreground">
                <span className="font-medium">{af.supervisor_name}</span>
                {af.valid_to && (
                  <span className="text-muted-foreground"> (bis {safeFormatDate(af.valid_to, 'd. MMM yyyy')})</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Meine aktiven Vertretungen */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Users className="w-4 h-4" />
          Meine Vertretung
        </h3>
        <Button
          size="sm"
          onClick={() => setDialogOpen(true)}
          className="bg-indigo-500 hover:bg-indigo-600"
          data-testid="activate-deputy-btn"
        >
          <UserCheck className="w-4 h-4 mr-1" />
          Vertretung aktivieren
        </Button>
      </div>

      {activeDeputies.length > 0 ? (
        <div className="space-y-2">
          {activeDeputies.map(dep => (
            <div
              key={dep.id}
              className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
              data-testid={`deputy-item-${dep.id}`}
            >
              <div>
                <p className="text-sm text-foreground font-medium">{dep.deputy_name}</p>
                <p className="text-xs text-muted-foreground">
                  {dep.valid_from && dep.valid_to
                    ? `${safeFormatDate(dep.valid_from, 'd. MMM')} – ${safeFormatDate(dep.valid_to, 'd. MMM yyyy')}`
                    : 'Unbefristet aktiv'}
                  {dep.note && ` • ${dep.note}`}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDeactivate(dep.id)}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid={`deactivate-deputy-${dep.id}`}
              >
                <UserX className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Keine aktive Vertretung eingerichtet</p>
      )}

      {/* Aktivierungs-Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Vertretung aktivieren
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Wählen Sie einen Kollegen, der während Ihrer Abwesenheit Ihr Team genehmigen kann.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Vertreter</Label>
              <Select
                value={formData.deputy_id}
                onValueChange={(value) => setFormData({ ...formData, deputy_id: value })}
              >
                <SelectTrigger className="bg-input border-border text-foreground" data-testid="deputy-select">
                  <SelectValue placeholder="Vertreter auswählen" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {supervisors.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.first_name} {s.last_name} ({s.role === 'admin' ? 'Admin' : 'Vorgesetzter'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Von (optional)</Label>
                <Input
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Bis (optional)</Label>
                <Input
                  type="date"
                  value={formData.valid_to}
                  onChange={(e) => setFormData({ ...formData, valid_to: e.target.value })}
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Notiz (optional)</Label>
              <Input
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                className="bg-input border-border text-foreground"
                placeholder="z.B. Urlaub, Dienstreise"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-muted-foreground">
              Abbrechen
            </Button>
            <Button onClick={handleActivate} className="bg-indigo-500 hover:bg-indigo-600">
              Aktivieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================
// FESTSETZUNG KOMPONENTEN
// ============================================================

/**
 * FixedWeekBanner - Anzeige wenn Woche festgesetzt wurde
 */
export const FixedWeekBanner = ({ approval, onObject }) => {
  const [showObjectDialog, setShowObjectDialog] = useState(false);
  const [objectionReason, setObjectionReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFixed = approval?.status === 'fixed_by_supervisor';
  const isFinal = approval?.status === 'final';
  if (!isFixed && !isFinal) return null;

  const fmtDate = (v) => (v ? formatDateTimeBerlin(v, 'dd.MM.yyyy') : '?');

  const handleSubmitObjection = async () => {
    if (!objectionReason || objectionReason.trim().length < 10) {
      toast.error('Bitte geben Sie eine Begründung ein (mind. 10 Zeichen)');
      return;
    }

    setIsSubmitting(true);
    try {
      await onObject(objectionReason.trim());
      setShowObjectDialog(false);
      setObjectionReason('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Einspruch konnte nicht eingelegt werden'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-3" data-testid="fixed-week-banner">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-amber-800 dark:text-amber-300">
              Woche verbindlich festgesetzt am {fmtDate(approval.fixed_at)}
            </p>
            {approval.fix_reason && (
              <p className="text-sm text-muted-foreground mt-1">Begründung: {approval.fix_reason}</p>
            )}
          </div>
        </div>

        {isFixed && !approval.escalated_to_management && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowObjectDialog(true)}
            className="border-amber-500/30 text-amber-800 dark:text-amber-300 hover:bg-amber-500/10"
            data-testid="object-btn"
          >
            <MessageSquare className="w-4 h-4 mr-1" />
            Einspruch einlegen
          </Button>
        )}

        {approval.escalated_to_management && !approval.escalation_resolved && (
          <div className="p-2 rounded bg-amber-500/10 border border-amber-500/30" data-testid="objection-pending">
            <p className="text-sm text-amber-800 dark:text-amber-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Einspruch eingelegt – Warte auf Entscheidung der Geschäftsführung
            </p>
            {approval.objection_reason && (
              <p className="text-xs text-muted-foreground mt-1">Ihr Einspruch: {approval.objection_reason}</p>
            )}
          </div>
        )}

        {approval.escalation_resolved && (
          <div
            className={`p-2 rounded border ${approval.escalation_decision === 'uphold' ? 'bg-amber-500/10 border-amber-500/30' : 'bg-emerald-500/10 border-emerald-500/30'}`}
            data-testid="escalation-result"
          >
            <p className={`text-sm flex items-center gap-2 ${approval.escalation_decision === 'uphold' ? 'text-amber-800 dark:text-amber-300' : 'text-emerald-800 dark:text-emerald-300'}`}>
              {approval.escalation_decision === 'uphold' ? (
                <>
                  <XCircle className="w-4 h-4" />
                  Einspruch abgelehnt – Festsetzung bestätigt
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Einspruch angenommen – Werte korrigiert
                </>
              )}
            </p>
            {approval.escalation_reason && (
              <p className="text-xs text-muted-foreground mt-1">Begründung: {approval.escalation_reason}</p>
            )}
          </div>
        )}

        {isFinal && (
          <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="week-final-hint">
            <Gavel className="w-4 h-4" />
            Die Woche ist abgeschlossen{approval.escalation_resolved_at ? ` (${fmtDate(approval.escalation_resolved_at)})` : ''}.
          </p>
        )}
      </div>

      {/* Einspruch-Dialog */}
      <Dialog open={showObjectDialog} onOpenChange={setShowObjectDialog}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              Einspruch einlegen
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Ihr Einspruch wird an die Geschäftsführung zur Klärung weitergeleitet.
              Die festgesetzten Werte bleiben bis zur Entscheidung unverändert.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Begründung (Pflichtfeld)</Label>
              <Textarea
                value={objectionReason}
                onChange={(e) => setObjectionReason(e.target.value)}
                className="bg-input border-border text-foreground resize-none"
                placeholder="Bitte begründen Sie Ihren Einspruch..."
                rows={4}
                data-testid="objection-reason"
              />
              <p className="text-xs text-muted-foreground">Mindestens 10 Zeichen</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setShowObjectDialog(false)} className="text-muted-foreground">
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleSubmitObjection}
              disabled={isSubmitting || objectionReason.trim().length < 10}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="submit-objection-btn"
            >
              {isSubmitting ? 'Wird eingereicht...' : 'Einspruch einreichen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * FixApprovalDialog - Vorgesetzter setzt Woche verbindlich fest
 */
export const FixApprovalDialog = ({ open, onOpenChange, approvalId, onSuccess }) => {
  const { api } = useAuth();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFix = async () => {
    if (!reason || reason.length < 10) {
      toast.error('Bitte geben Sie eine Begründung ein (mind. 10 Zeichen)');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(`/approvals/${approvalId}/fix`, { reason });
      toast.success('Woche verbindlich festgesetzt');
      onSuccess?.();
      onOpenChange(false);
      setReason('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Festsetzen';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Gavel className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            Verbindlich festsetzen
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Korrigieren Sie die Einträge falls nötig und setzen Sie die Woche dann verbindlich fest.
            Der Mitarbeiter kann die Werte nicht mehr ändern.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-3 rounded bg-amber-500/10 border border-amber-500/20">
            <p className="text-sm text-amber-600 dark:text-amber-400">
              <strong>Hinweis:</strong> Diese Aktion sperrt alle Arbeitszeit-Einträge dieser Woche.
              Der Mitarbeiter kann Einspruch einlegen, der dann an die Geschäftsführung eskaliert wird.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground">Begründung (Pflichtfeld)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="bg-input border-border text-foreground resize-none"
              placeholder="Begründung für die verbindliche Festsetzung..."
              rows={4}
              data-testid="fix-reason"
            />
            <p className="text-xs text-muted-foreground">Mindestens 10 Zeichen. Wird im Audit-Log dokumentiert.</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">
            Abbrechen
          </Button>
          <Button
            onClick={handleFix}
            disabled={isSubmitting || reason.length < 10}
            className="bg-amber-500 hover:bg-amber-600"
            data-testid="confirm-fix-btn"
          >
            {isSubmitting ? 'Wird festgesetzt...' : 'Verbindlich festsetzen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * EscalationList - Liste der offenen Eskalationen für Admin
 */
export const EscalationList = () => {
  const { api, user } = useAuth();
  const [escalations, setEscalations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [resolveDialog, setResolveDialog] = useState({ open: false, escalation: null });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchEscalations();
    }
  }, [isAdmin]);

  const fetchEscalations = async () => {
    try {
      const response = await api.get('/approvals/escalations');
      setEscalations(response.data);
    } catch (error) {
      console.error('Failed to fetch escalations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (!isAdmin) return null;
  if (isLoading) return <div className="text-muted-foreground text-sm">Lade Eskalationen...</div>;

  if (escalations.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-600 dark:text-green-400/50" />
        <p>Keine offenen Eskalationen</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-foreground flex items-center gap-2">
        <Gavel className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        Offene Eskalationen ({escalations.length})
      </h3>

      {escalations.map(esc => (
        <div
          key={esc.id}
          className="rounded-lg border border-amber-500/30 bg-amber-500/5 overflow-hidden"
          data-testid={`escalation-${esc.id}`}
        >
          {/* Header */}
          <button
            onClick={() => toggleExpand(esc.id)}
            className="w-full flex items-center justify-between p-4 hover:bg-accent transition-colors"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <div className="text-left">
                <p className="font-medium text-foreground">
                  {esc.employee?.first_name} {esc.employee?.last_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  KW {safeFormatDate(esc.week_start, 'I')} · {safeFormatDate(esc.week_start, 'd.M.')} – {safeFormatDate(esc.week_end, 'd.M.yyyy')} · Festgesetzt von {esc.fixer?.first_name} {esc.fixer?.last_name}
                </p>
              </div>
            </div>
            {expanded[esc.id] ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </button>

          {/* Details */}
          {expanded[esc.id] && (
            <div className="p-4 pt-0 space-y-4 border-t border-border">
              {/* Festsetzung */}
              <div className="p-3 rounded bg-muted/50">
                <p className="text-sm font-medium text-muted-foreground mb-1">Festsetzung durch Vorgesetzten:</p>
                <p className="text-sm text-muted-foreground">{esc.fix_reason}</p>
                {esc.fixed_at && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDateTimeBerlin(esc.fixed_at, "d. MMM yyyy, HH:mm")}
                  </p>
                )}
              </div>

              {/* Einspruch */}
              <div className="p-3 rounded bg-red-500/10 border border-red-500/20">
                <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">Einspruch des Mitarbeiters:</p>
                <p className="text-sm text-muted-foreground">{esc.objection_reason}</p>
                {esc.objection_at && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDateTimeBerlin(esc.objection_at, "d. MMM yyyy, HH:mm")}
                  </p>
                )}
              </div>

              {/* Was der Vorgesetzte geändert hat */}
              {esc.admin_edits?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Vom Vorgesetzten geändert:</p>
                  <div className="space-y-1">
                    {esc.admin_edits.map((edit, idx) => (
                      <div key={edit.id || idx} className="text-sm p-2 rounded bg-muted/30">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-muted-foreground">{safeFormatDate(edit.entry_date, 'EEEE, d. MMM')}:</span>
                          <span className="text-red-600 dark:text-red-400 line-through">
                            {edit.original_values?.start_time} – {edit.original_values?.end_time} ({edit.original_values?.hours}h)
                          </span>
                          <span className="text-muted-foreground">&rarr;</span>
                          <span className="text-emerald-600 dark:text-emerald-400">
                            {edit.new_values?.start_time} – {edit.new_values?.end_time} ({edit.new_values?.hours}h)
                          </span>
                        </div>
                        {edit.comment && <p className="text-xs text-muted-foreground mt-1 italic">&bdquo;{edit.comment}&ldquo;</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Arbeitszeiten */}
              {esc.work_entries?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Alle Einträge der Woche (Stand nach Festsetzung):</p>
                  <div className="space-y-1">
                    {esc.work_entries.map(entry => (
                      <div key={entry.id} className="flex items-center justify-between text-sm p-2 rounded bg-muted/30">
                        <span className="text-muted-foreground">
                          {safeFormatDate(entry.date, 'EEEE, d. MMM')}
                        </span>
                        <span className="text-foreground">
                          {entry.start_time} – {entry.end_time} ({entry.hours}h)
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Aktionen */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => setResolveDialog({ open: true, escalation: esc, decision: 'uphold' })}
                  variant="outline"
                  className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid={`uphold-btn-${esc.id}`}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Festsetzung bestätigen
                </Button>
                <Button
                  onClick={() => setResolveDialog({ open: true, escalation: esc, decision: 'revise' })}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid={`revise-btn-${esc.id}`}
                >
                  <Edit3 className="w-4 h-4 mr-1" />
                  Werte korrigieren
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Resolve Dialog */}
      <ResolveEscalationDialog
        open={resolveDialog.open}
        onOpenChange={(open) => setResolveDialog({ ...resolveDialog, open })}
        escalation={resolveDialog.escalation}
        decision={resolveDialog.decision}
        onSuccess={fetchEscalations}
      />
    </div>
  );
};

/**
 * ResolveEscalationDialog - Admin entscheidet über Eskalation
 */
const ResolveEscalationDialog = ({ open, onOpenChange, escalation, decision, onSuccess }) => {
  const { api } = useAuth();
  const [reason, setReason] = useState('');
  const [newValues, setNewValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open && escalation?.work_entries) {
      // Initialisiere newValues mit aktuellen Werten
      const initial = {};
      escalation.work_entries.forEach(entry => {
        initial[entry.id] = {
          start_time: entry.start_time,
          end_time: entry.end_time,
          hours: entry.hours,
        };
      });
      setNewValues(initial);
    }
  }, [open, escalation]);

  const handleResolve = async () => {
    if (!reason || reason.length < 10) {
      toast.error('Bitte geben Sie eine Begründung ein (mind. 10 Zeichen)');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        decision,
        reason,
        new_values: decision === 'revise' ? newValues : null,
      };

      await api.post(`/approvals/${escalation.id}/escalation/resolve`, payload);
      toast.success(decision === 'uphold' ? 'Festsetzung bestätigt' : 'Werte korrigiert');
      onSuccess?.();
      onOpenChange(false);
      setReason('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Entscheiden';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateEntryValue = (entryId, field, value) => {
    setNewValues(prev => ({
      ...prev,
      [entryId]: {
        ...prev[entryId],
        [field]: value,
      }
    }));
  };

  if (!escalation) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            {decision === 'uphold' ? (
              <>
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                Festsetzung bestätigen
              </>
            ) : (
              <>
                <Edit3 className="w-5 h-5 text-green-600 dark:text-green-400" />
                Werte korrigieren
              </>
            )}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {decision === 'uphold'
              ? 'Die Festsetzung des Vorgesetzten wird bestätigt, der Einspruch zurückgewiesen.'
              : 'Korrigieren Sie die Werte und geben Sie eine Begründung an.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Bei revise: Werte bearbeiten */}
          {decision === 'revise' && escalation.work_entries?.map(entry => (
            <div key={entry.id} className="p-3 rounded bg-muted/50 space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                {safeFormatDate(entry.date, 'EEEE, d. MMM')}
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Von</Label>
                  <Input
                    type="time"
                    value={newValues[entry.id]?.start_time || entry.start_time}
                    onChange={(e) => updateEntryValue(entry.id, 'start_time', e.target.value)}
                    className="bg-input border-border text-foreground text-sm h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Bis</Label>
                  <Input
                    type="time"
                    value={newValues[entry.id]?.end_time || entry.end_time}
                    onChange={(e) => updateEntryValue(entry.id, 'end_time', e.target.value)}
                    className="bg-input border-border text-foreground text-sm h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Stunden</Label>
                  <Input
                    type="number"
                    step="0.25"
                    value={newValues[entry.id]?.hours || entry.hours}
                    onChange={(e) => updateEntryValue(entry.id, 'hours', parseFloat(e.target.value))}
                    className="bg-input border-border text-foreground text-sm h-8"
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="space-y-2">
            <Label className="text-muted-foreground">Begründung (Pflichtfeld)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="bg-input border-border text-foreground resize-none"
              placeholder={decision === 'uphold'
                ? 'Begründung für die Bestätigung der Festsetzung...'
                : 'Begründung für die Korrektur...'}
              rows={3}
              data-testid="resolve-reason"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-muted-foreground">
            Abbrechen
          </Button>
          <Button
            onClick={handleResolve}
            disabled={isSubmitting || reason.length < 10}
            className={decision === 'uphold' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
            data-testid="confirm-resolve-btn"
          >
            {isSubmitting ? 'Wird gespeichert...' : (decision === 'uphold' ? 'Festsetzung bestätigen' : 'Korrigieren & speichern')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default {
  DeputyActivationPanel,
  FixedWeekBanner,
  FixApprovalDialog,
  EscalationList,
};
